import dayjs, { type Dayjs } from "dayjs";
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import type React from "react";
import { useCallback, useMemo, useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { LANG_JP, WEEKDAYS_EN_SHORT, WEEKDAYS_JP_SHORT } from "@/constants";
import { cn } from "@/lib/utils";
import { isToday } from "@/lib/utils/dateUtils";

interface CustomDatePickerProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  labelId?: string;
  className?: string;
  triggerClassName?: string;
  side?: "top" | "right" | "bottom" | "left";
  id?: string;
  ariaLabel?: string;
  minDate?: string;
  maxDate?: string;
  disabled?: boolean;
  isDateDisabled?: (date: Dayjs) => boolean;
  showMonthPicker?: boolean;
  triggerOnKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;
}

export const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  value = "",
  onChange,
  placeholder = "",
  label,
  labelId,
  className,
  triggerClassName,
  side = "top",
  id,
  ariaLabel,
  minDate,
  maxDate,
  disabled = false,
  isDateDisabled,
  showMonthPicker = false,
  triggerOnKeyDown,
}) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(dayjs());

  // dayjsは自動でロケール処理するため設定不要

  const selectedDate = useMemo(() => {
    return value && dayjs(value).isValid() ? dayjs(value) : null;
  }, [value]);

  const minDateObj = useMemo(() => {
    return minDate && dayjs(minDate).isValid() ? dayjs(minDate) : null;
  }, [minDate]);

  const maxDateObj = useMemo(() => {
    return maxDate && dayjs(maxDate).isValid() ? dayjs(maxDate) : null;
  }, [maxDate]);

  const displayValue = useMemo(() => {
    if (!selectedDate) return "";
    // i18n.languageに応じて表示形式を変更
    const format =
      i18n.language === LANG_JP ? "YYYY年MM月DD日" : "MMM DD, YYYY";
    return selectedDate.format(format);
  }, [selectedDate, i18n.language]);

  // カレンダー表示用の日付配列生成
  const calendarDays = useMemo(() => {
    const startOfMonth = currentMonth.startOf("month");
    const endOfMonth = currentMonth.endOf("month");
    const startOfCalendar = startOfMonth.startOf("week");
    const endOfCalendar = endOfMonth.endOf("week");

    const days = [];
    let current = startOfCalendar;

    while (
      current.isBefore(endOfCalendar) ||
      current.isSame(endOfCalendar, "day")
    ) {
      days.push(current);
      current = current.add(1, "day");
    }

    return days;
  }, [currentMonth]);

  const handleDateSelect = useCallback(
    (date: dayjs.Dayjs) => {
      onChange(date.format("YYYY-MM-DD"));
      setIsOpen(false);
    },
    [onChange],
  );

  const handlePrevMonth = useCallback(() => {
    setCurrentMonth((prev) => prev.subtract(1, "month"));
  }, []);

  const handleNextMonth = useCallback(() => {
    setCurrentMonth((prev) => prev.add(1, "month"));
  }, []);

  const handlePrevYear = useCallback(() => {
    setCurrentMonth((prev) => prev.subtract(1, "year"));
  }, []);

  const handleNextYear = useCallback(() => {
    setCurrentMonth((prev) => prev.add(1, "year"));
  }, []);

  const monthYearDisplay = useMemo(() => {
    if (showMonthPicker) {
      return i18n.language === LANG_JP
        ? currentMonth.format("YYYY年")
        : currentMonth.format("YYYY");
    }
    if (i18n.language === LANG_JP) {
      return currentMonth.format("YYYY年MM月");
    }
    return currentMonth.format("MMMM YYYY");
  }, [currentMonth, i18n.language, showMonthPicker]);

  const weekDays = useMemo(() => {
    if (i18n.language === LANG_JP) {
      return WEEKDAYS_JP_SHORT;
    }
    return WEEKDAYS_EN_SHORT;
  }, [i18n.language]);

  return (
    <div className={cn("relative", className)}>
      {label && (
        <div className="absolute -top-3 left-3 z-10">
          <span className="bg-white px-1 text-sm font-medium text-gray-500">
            {label}
          </span>
        </div>
      )}

      <Popover open={isOpen && !disabled} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            aria-label={ariaLabel || placeholder || "Select date"}
            aria-labelledby={labelId}
            aria-haspopup="dialog"
            aria-expanded={isOpen}
            onKeyDown={triggerOnKeyDown}
            variant="outline"
            className={cn("w-full justify-start", triggerClassName)}
            disabled={disabled}
          >
            <CalendarIcon className="mr-2 size-4" />
            {displayValue || placeholder}
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="z-[var(--z-floating-ui)] w-auto rounded-md border bg-white p-0 shadow-lg"
          side={side}
          align="start"
        >
          <div className="p-3">
            {/* 月/年ナビゲーション */}
            <div className="mb-4 flex items-center justify-between">
              <Button
                variant="outline"
                size="sm"
                onClick={showMonthPicker ? handlePrevYear : handlePrevMonth}
              >
                <ChevronLeftIcon className="size-4" />
              </Button>

              <div className="flex flex-col items-center gap-1">
                <div className="text-sm font-medium">{monthYearDisplay}</div>
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={showMonthPicker ? handleNextYear : handleNextMonth}
              >
                <ChevronRightIcon className="size-4" />
              </Button>
            </div>

            {showMonthPicker ? (
              <div className="grid grid-cols-3 gap-2">
                {Array.from({ length: 12 }, (_, index) => {
                  const monthDate = currentMonth.month(index).date(1);
                  const monthLabel =
                    i18n.language === LANG_JP
                      ? `${index + 1}月`
                      : monthDate.format("MMM");
                  const daysInMonth = monthDate.daysInMonth();
                  const hasEnabledDate = Array.from(
                    { length: daysInMonth },
                    (_, dayIndex) => {
                      const date = monthDate.date(dayIndex + 1);
                      const isBeforeMin = minDateObj
                        ? date.isBefore(minDateObj, "day")
                        : false;
                      const isAfterMax = maxDateObj
                        ? date.isAfter(maxDateObj, "day")
                        : false;
                      const customDisabled = isDateDisabled
                        ? isDateDisabled(date)
                        : false;
                      return !(isBeforeMin || isAfterMax || customDisabled);
                    },
                  ).some(Boolean);
                  const isDisabled = !hasEnabledDate;
                  const isSelected =
                    selectedDate &&
                    selectedDate.year() === monthDate.year() &&
                    selectedDate.month() === monthDate.month();

                  return (
                    <Button
                      key={monthDate.format("YYYY-MM")}
                      variant="ghost"
                      size="sm"
                      className={cn(
                        "justify-center",
                        isSelected &&
                          "bg-rw-main text-white hover:bg-rw-main-hover",
                        isDisabled && "!text-gray-300 hover:bg-transparent",
                      )}
                      onClick={() => {
                        if (isDisabled) return;
                        onChange(monthDate.format("YYYY-MM-DD"));
                        setCurrentMonth(monthDate);
                        setIsOpen(false);
                      }}
                      disabled={isDisabled}
                    >
                      {monthLabel}
                    </Button>
                  );
                })}
              </div>
            ) : (
              <>
                {/* 曜日ヘッダー */}
                <div className="mb-2 grid grid-cols-7 gap-1">
                  {weekDays.map((day) => (
                    <div
                      key={day}
                      className="flex size-8 items-center justify-center text-xs font-medium text-gray-500"
                    >
                      {day}
                    </div>
                  ))}
                </div>

                {/* カレンダーグリッド */}
                <div className="grid grid-cols-7 gap-1">
                  {calendarDays.map((date) => {
                    const isCurrentMonth =
                      date.month() === currentMonth.month();
                    const isSelected = selectedDate?.isSame(date, "day");
                    const isBeforeMin = minDateObj
                      ? date.isBefore(minDateObj, "day")
                      : false;
                    const isAfterMax = maxDateObj
                      ? date.isAfter(maxDateObj, "day")
                      : false;
                    const customDisabled = isDateDisabled
                      ? isDateDisabled(date)
                      : false;
                    const isDisabled =
                      isBeforeMin || isAfterMax || customDisabled;

                    return (
                      <Button
                        key={date.format("YYYY-MM-DD")}
                        variant="ghost"
                        size="sm"
                        className={cn(
                          !isCurrentMonth && "text-gray-300",
                          isCurrentMonth && "text-gray-900 hover:bg-gray-100",
                          isSelected &&
                            "bg-rw-main text-white hover:bg-rw-main-hover",
                          isToday(date.year(), date.month(), date.date()) &&
                            !isSelected &&
                            "bg-gray-100 font-medium",
                          isDisabled && "!text-gray-300 hover:bg-transparent",
                        )}
                        onClick={() => {
                          handleDateSelect(date);
                        }}
                        disabled={isDisabled}
                      >
                        {date.date()}
                      </Button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
